import {
  Box,
  Page,
  PageBody,
  PageHeader,
  PageHeaderTitle,
  Panel,
  Spinner,
  Stack,
} from '@customerio/pluma-components/react';
import { DocumentTitle } from '@/components/page/document-title';
import { LoadingPage } from '@/components/page/loading';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { MergeActions } from './components/merge-actions';
import { MergeStepper } from './components/merge-stepper';
import { MergeToolbar, ReviewStep, SetupStep } from './components/merge-steps';
import { useMergeWizard, useMergeWorkspaceContext } from './hooks';
import { Step, UNCONFIRMED, type MergeWorkspaceContext } from './types';

type MergeWizardProps = {
  workspaceId: number;
  context: MergeWorkspaceContext | null;
};

function MergeWizard({ workspaceId, context }: MergeWizardProps) {
  const wizard = useMergeWizard({ workspaceId, context });
  const taxonomy = useProductTaxonomy();
  const pageTitle = `Merge ${taxonomy.term('people.plural')}`;

  if (wizard.error != null) {
    throw wizard.error;
  }
  if (!wizard.isReady || context == null) {
    return <LoadingPage />;
  }

  const { step, selected } = wizard;
  const isSetup = step.id === Step.Setup;
  const confirmations = step.id === Step.Preview ? step.confirmations : UNCONFIRMED;
  const cardProps = {
    workspaceId,
    selected,
    context,
    searchTerm: wizard.searchTerm,
    isLoadingMergedAttributes: wizard.isPreviewing,
    mergedAttributes: wizard.mergedAttributes,
    mergeSummary: wizard.mergeSummary,
  };

  return (
    <Page>
      <DocumentTitle title={pageTitle} />
      <PageHeader>
        <PageHeaderTitle>{pageTitle}</PageHeaderTitle>
      </PageHeader>

      <PageBody>
        <Stack gap="200">
          <Box mb="100">
            <MergeStepper
              activeStep={step.id}
              hasRequiredSetup={wizard.hasRequiredSetup}
              onChangeStep={wizard.changeStep}
            />
          </Box>

          <Panel withSectionBorders>
            {!isSetup && wizard.isPreviewing ? (
              <Stack alignBlock="center" alignInline="center" p="600">
                <Spinner shouldShowLoadingText label="Fetching preview…" />
              </Stack>
            ) : (
              <>
                <MergeToolbar
                  searchTerm={wizard.searchTerm}
                  isSearchHidden={wizard.noCustomersSelected && isSetup}
                  isSwapShown={isSetup}
                  isSwapDisabled={wizard.noCustomersSelected}
                  onSearchTermChange={wizard.changeSearchTerm}
                  onSwap={wizard.swapCustomers}
                />

                {isSetup ? (
                  <SetupStep cardProps={cardProps} cardKeys={wizard.cardKeys} onSelect={wizard.selectCustomer} />
                ) : (
                  <ReviewStep
                    cardProps={cardProps}
                    confirmations={confirmations}
                    canMerge={wizard.canMerge}
                    onChangeConfirmations={wizard.setConfirmations}
                    onRequestFeedback={wizard.showMergeFeedbackModal}
                  />
                )}
              </>
            )}
          </Panel>

          <MergeActions
            activeStep={step.id}
            canMerge={wizard.canMerge}
            hasRequiredSetup={wizard.hasRequiredSetup}
            isPreviewing={wizard.isPreviewing}
            isMerging={wizard.isMerging}
            confirmations={confirmations}
            cancelHref={wizard.cancelHref}
            onNext={wizard.previewAndGoToNextStep}
            onBack={wizard.goToSetupStep}
            onMerge={() => void wizard.confirmAndMerge()}
          />
        </Stack>
      </PageBody>
    </Page>
  );
}

/** The merge-two-people wizard: pick a primary and a secondary, review what the merge keeps and adds, confirm. */
export default function PeopleMergePage() {
  const workspace = useCurrentWorkspace();
  const workspaceId = workspace?.id ?? 0;
  const context = useMergeWorkspaceContext(workspaceId);

  if (workspace == null) {
    return <LoadingPage />;
  }

  return <MergeWizard workspaceId={workspace.id} context={context} />;
}
